import React, { SyntheticEvent, useCallback, useState } from 'react';
import { RefCallBack } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage } from 'react-intl';

import { AMCR_PROJECT_ID_PATTERN } from '../../../consts';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceAutocomplete,
} from '../../../components/design-system';

interface AmcrProjectIdsFieldProps {
  value: string[];
  inputRef: RefCallBack;
  onBlur: () => void;
  onChange: (value: string[]) => void;
}

const NO_OPTIONS: string[] = [];
const addButtonStyles = { alignSelf: 'flex-start' };
const addIcon = <AddIcon />;

const toAmcrProjectId = (value: string) => value.trim().toUpperCase();

/** Re-adding an ID moves it to the end, where the active (newest) ID is kept. */
const keepLastOccurrences = (ids: string[]) =>
  ids.filter((id, index) => ids.lastIndexOf(id) === index);

const AmcrProjectIdsField = React.memo(
  ({ value, inputRef, onBlur, onChange }: AmcrProjectIdsFieldProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [invalidId, setInvalidId] = useState<string | null>(null);

    const handleOpen = () => {
      setIsOpen(true);
    };

    const handleChange = useCallback(
      (_event: SyntheticEvent, values: string[]) => {
        const ids = values.map(toAmcrProjectId).filter(Boolean);
        const invalid = ids.find((id) => !AMCR_PROJECT_ID_PATTERN.test(id));

        setInvalidId(invalid ?? null);
        setIsOpen(true);

        if (invalid) {
          return;
        }

        onChange(keepLastOccurrences(ids));
      },
      [onChange]
    );

    const helperText = invalidId ? (
      <FormattedMessage
        id="site.amcrProjectIds.invalid"
        defaultMessage="“{id}” is not a valid PAS project ID, e.g. C-202602217."
        values={{ id: invalidId }}
      />
    ) : (
      <FormattedMessage
        id="site.amcrProjectIds.help"
        defaultMessage="Press Enter to add an ID. New PAS finds use the last one."
      />
    );

    const textFieldProps = {
      id: 'site-amcr-project-ids',
      inputRef,
      onBlur,
      // Focus only when revealed by the button; existing IDs render open.
      autoFocus: isOpen,
      error: Boolean(invalidId),
      helperText,
      placeholder: value.length === 0 ? 'C-202602217' : undefined,
    };

    if (!isOpen && value.length === 0) {
      return (
        <Button
          tone="neutral"
          density="compact"
          startIcon={addIcon}
          onClick={handleOpen}
          sx={addButtonStyles}
        >
          <FormattedMessage
            id="site.amcrProjectIds.add"
            defaultMessage="Add PAS ID"
          />
        </Button>
      );
    }

    return (
      <FormField>
        <FieldLabel htmlFor="site-amcr-project-ids">
          <FormattedMessage
            id="site.field.amcrProjectIds"
            defaultMessage="PAS project IDs"
          />
        </FieldLabel>
        <SurfaceAutocomplete<string, true, false, true>
          freeSolo
          multiple
          autoSelect
          options={NO_OPTIONS}
          value={value}
          onChange={handleChange}
          surface="sunken"
          textFieldProps={textFieldProps}
        />
      </FormField>
    );
  }
);

AmcrProjectIdsField.displayName = 'AmcrProjectIdsField';

export { AmcrProjectIdsField };
